<template><div><code-well-header bgclass="d-bgc-primary">
  <example-notice kind="base" title="Base title (optional)" />
</code-well-header>
<!-- <component-combinator component-name="DtNotice" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A notice delivers informational and assistive messages that inform the user about product or account statuses and related actions. It can suggest an action or dismissed by the user, though neither are required. For time-based notifications, see <RouteLink to="/components/toast.html" class="d-docsite--link d-link">Toast</RouteLink>.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Used in most scenarios when the message should be noticeable but not dominate.</p>
<code-well-header bgclass="d-bgc-primary">
  <example-notice kind="base" title="Base title (optional)" />
  <example-notice kind="error" title="Error title (optional)" />
  <example-notice kind="info" title="Info title (optional)" />
  <example-notice kind="success" title="Success title (optional)" />
  <example-notice kind="warning" title="Warning title (optional)" />
</code-well-header>
<code-example-tabs
htmlCode='
<aside class="d-notice d-notice--base">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Base title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
      <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--info">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Info title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
      <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--error">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Error title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
      <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--success">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Success title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
      <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--warning">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Warning title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
      <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
'
vueCode='
<dt-notice
  title="Base title (optional)"
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Info title (optional)"
  kind="info"
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Error title (optional)"
  kind="error"
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Success title (optional)"
  kind="success"
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Warning title (optional)"
  kind="warning"
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
'
showHtmlWarning />
<h3 id="important" tabindex="-1" class="d-docsite--header-3">Important <a class="header-anchor d-link d-docsite--link d-link" href="#important" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Used occasionally in scenarios when the message needs to dominate.</p>
<code-well-header>
  <example-notice important kind="base" title="Base title (optional)" />
  <example-notice important kind="error" title="Error title (optional)" />
  <example-notice important kind="info" title="Info title (optional)" />
  <example-notice important kind="success" title="Success title (optional)" />
  <example-notice important kind="warning" title="Warning title (optional)" />
</code-well-header>
<code-example-tabs
htmlCode='
<aside class="d-notice d-notice--base d-notice--important">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Base title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
        <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--info d-notice--important">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Info title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
        <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--error d-notice--important">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Error title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
        <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--success d-notice--important">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Success title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
        <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
<aside class="d-notice d-notice--warning d-notice--important">
  <div aria-hidden="true" class="d-notice__icon">
    <span class="d-icon__wrapper">
      <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-400" style="display: none;">
        <div
          class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
          style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
        ></div>
      </div>
      <svg>...</svg>
    </span>
  </div>
  <div class="d-notice__content" role="status">
    <p class="d-notice__title">Warning title (optional)</p>
    <p class="d-notice__message">
      <span> Message body with <a href="#" class="d-link d-link--muted">a link</a>. </span>
    </p>
  </div>
  <div class="d-notice__actions">
    <button type="button" class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--sm">
        <span class="d-btn__label base-button__label"> Action </span>
    </button>
    <button type="button" aria-label="Close" class="base-button__button d-btn d-btn--sm d-btn--circle d-btn--icon-only">
      <span class="base-button__icon d-btn__icon d-btn__icon--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-200" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </span>
    </button>
  </div>
</aside>
'
vueCode='
<dt-notice
  title="Base title (optional)"
  important
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="inverted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Info title (optional)"
  kind="info"
  important
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="inverted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Error title (optional)"
  kind="error"
  important
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="inverted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Success title (optional)"
  kind="success"
  important
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="inverted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
<dt-notice
  title="Warning title (optional)"
  kind="warning"
  important
>
  <span>
    Message body with
    <a
      href="#"
      class="d-link"
      :class="linkClass"
    >a link</a>.
  </span>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="inverted"
      @click="onClick"
    >
      Action
    </dt-button>
  </template>
</dt-notice>
'
showHtmlWarning />
<h3 id="truncate-text" tabindex="-1" class="d-docsite--header-3">Truncate Text <a class="header-anchor d-link d-docsite--link d-link" href="#truncate-text" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Truncates the text instead of wrapping it. Useful when the Notice needs to have a fixed height.</p>
<code-well-header bgclass="d-bgc-primary">
  <dt-notice
    ref="htmlTruncateText"
    :truncate-text="true"
    title="Lorem ipsum dolor sit amet, consectetur adipiscing elit,
      sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
  >
    <span>
      Duis aute irure dolor in reprehenderit in voluptate velit
      esse cillum dolore eu fugiat nulla pariatur.
    </span>
  </dt-notice>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.htmlTruncateText'
vueCode='
<dt-notice
  :truncate-text="true"
  title="Lorem ipsum dolor sit amet, consectetur adipiscing elit,
    sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
>
  <span>
    Duis aute irure dolor in reprehenderit in voluptate velit
    esse cillum dolore eu fugiat nulla pariatur.
  </span>
</dt-notice>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="notice" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="notice" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<component-accessible-table component-name="notice" /></div></template>


<script setup>
  import ExampleNotice from '@exampleComponents/ExampleNotice.vue';
</script>